import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, CloudArrowUp, Warning } from 'shared/icons';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import StandardTable from '../../components/ui/StandardTable';
import { useToast } from '../../components/ui/Toast';
import WmsNavigation from '../../components/wms/WmsNavigation';
import { api } from '../../services/api';
import { FormatFileSize } from '../../utils/format';

const OutboundCreatePage = () => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const toast = useToast();

    const buildOutboundFormData = () => {
        const formData = new FormData();
        if (file) {
            formData.append('pdf', file);
        }
        return formData;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!file) {
            toast.error('Selecione o arquivo PDF do romaneio');
            return;
        }

        if (file.type !== 'application/pdf') {
            toast.error('O arquivo deve ser um PDF valido');
            return;
        }

        // Validacao de TAMANHO no front (antes so o backend barrava). 10MB = limite do backend.
        if (file.size > 10 * 1024 * 1024) {
            toast.error(`O PDF deve ter no maximo 10MB (atual: ${FormatFileSize(file.size)})`);
            return;
        }

        setLoading(true);
        const formData = buildOutboundFormData();

        try {
            const data = await api.createOutbound(formData, { previewOnly: true });
            setResult(data);
        } catch (error) {
            toast.error(error.message || 'Erro ao processar outbound');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmStart = async () => {
        if (!file) {
            toast.error('Selecione o PDF novamente para abrir a solicitacao.');
            return;
        }

        try {
            setLoading(true);
            const formData = buildOutboundFormData();
            const created = await api.createOutbound(formData, { previewOnly: false });
            if (!created?.outbound_id) {
                throw new Error('Nao foi possivel iniciar a solicitacao.');
            }

            toast.success('Solicitacao iniciada com sucesso.');
            navigate(`/wms/outbound/${created.outbound_id}`);
        } catch (error) {
            toast.error(error.message || 'Erro ao iniciar solicitacao');
        } finally {
            setLoading(false);
        }
    };

    const detailColumns = useMemo(
        () => [
            {
                key: 'sku',
                label: 'SKU',
                sortable: false,
                getValue: (row) => row.sku || '-',
                cellClassName: 'font-mono font-bold text-black dark:text-white',
            },
            {
                key: 'description',
                label: 'Descricao',
                sortable: false,
                getValue: (row) => row.description || '-',
                cellClassName: 'text-mono-700 dark:text-mono-300',
            },
            {
                key: 'ean',
                label: 'EAN',
                sortable: false,
                getValue: (row) => row.ean || '-',
                cellClassName: 'font-mono text-black dark:text-white',
            },
            {
                key: 'units',
                label: 'Unidades',
                align: 'center',
                sortable: false,
                searchable: false,
                getValue: (row) => Number(row.units || 0),
            },
            {
                key: 'units_per_pallet',
                label: 'Unid./pallet',
                align: 'center',
                sortable: false,
                searchable: false,
                getValue: (row) => row.units_per_pallet || '-',
            },
            {
                key: 'pallets_needed',
                label: 'Pallets necessarios',
                align: 'right',
                sortable: false,
                searchable: false,
                getValue: (row) => Number(row.pallets_needed || 0).toFixed(2),
                cellClassName: 'font-semibold text-black dark:text-white',
            },
            {
                key: 'error',
                label: 'Validacao',
                sortable: false,
                searchable: true,
                getValue: (row) => row.error_message || '-',
                render: (row) =>
                    row.status === 'error' ? (
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">
                            {row.error_message || 'Nao cadastrado no sistema'}
                        </span>
                    ) : (
                        <span className="text-xs text-mono-500 dark:text-mono-400">OK</span>
                    ),
            },
        ],
        [],
    );

    if (result) {
        const hasValidationErrors = Boolean(result.hasValidationErrors);
        const isBlocked = hasValidationErrors;
        return (
            <div className="space-y-6">
                <PageHeader
                    title="Resultado da analise"
                    subtitle="Validacao do romaneio para abertura do outbound"
                    actions={
                        <Button variant="ghost" size="sm" onClick={() => setResult(null)}>
                            <ArrowLeft size={18} className="mr-2" />
                            Voltar
                        </Button>
                    }
                />

                <div
                    className={`p-6 rounded-xl border ${isBlocked ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'}`}
                >
                    <div className="flex items-start gap-4">
                        <div
                            className={`p-2 rounded-full ${isBlocked ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}
                        >
                            {isBlocked ? (
                                <Warning size={32} className="text-red-600 dark:text-red-400" weight="fill" />
                            ) : (
                                <CheckCircle size={32} className="text-green-600 dark:text-green-400" weight="fill" />
                            )}
                        </div>
                        <div className="flex-1">
                            <h2
                                className={`text-xl font-bold mb-2 ${isBlocked ? 'text-red-800 dark:text-red-300' : 'text-green-800 dark:text-green-300'}`}
                            >
                                {hasValidationErrors ? 'Carga bloqueada por itens invalidos' : 'Carga aprovada'}
                            </h2>
                            <div className="space-y-1 text-mono-700 dark:text-mono-300">
                                <p>
                                    Pallets estimados:{' '}
                                    <strong>{Number(result.totalPalletsNeeded || 0).toFixed(2)}</strong>
                                </p>
                            </div>

                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                {result?.requires_confirmation ? (
                                    <>
                                        <Button onClick={handleConfirmStart} loading={loading}>
                                            Abrir solicitacao
                                        </Button>
                                        <Button variant="secondary" onClick={() => setResult(null)}>
                                            Cancelar
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        onClick={() =>
                                            result?.outbound_id
                                                ? navigate(`/wms/outbound/${result.outbound_id}`)
                                                : navigate('/wms')
                                        }
                                        variant={isBlocked ? 'secondary' : 'primary'}
                                    >
                                        {result?.outbound_id ? 'Abrir outbound' : 'Ir para dashboard'}
                                    </Button>
                                )}
                                {hasValidationErrors ? (
                                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                                        <Warning size={14} className="mr-1" /> Corrija os SKUs sem unidades/pallet e
                                        tente novamente.
                                    </p>
                                ) : null}
                                {result?.requires_confirmation ? (
                                    <p className="text-sm text-mono-600 dark:text-mono-400">
                                        Revise os dados e confirme para abrir o outbound.
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                <StandardTable
                    rows={result.items || []}
                    columns={detailColumns}
                    rowKey={(_, index) => index}
                    variant="minimal"
                    emptyMessage="Nenhum item identificado no PDF."
                    rowClassName={(row) => (row.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' : '')}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6 text-black dark:text-white">
            <PageHeader title="Novo outbound" subtitle="Envie o romaneio em PDF para analisar e abrir a solicitacao" />

            <WmsNavigation />

            <div className="max-w-xl mx-auto py-8">
                <Card title="Criar ordem de outbound">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-1.5">
                            <label
                                htmlFor="outbound-pdf-upload"
                                className="block text-sm font-semibold text-mono-700 dark:text-mono-300"
                            >
                                Romaneio (PDF)
                            </label>
                            <div className="border-2 border-dashed border-mono-200 dark:border-mono-800 rounded-xl p-8 text-center hover:border-mono-400 dark:hover:border-mono-600 transition-all cursor-pointer group relative">
                                <input
                                    id="outbound-pdf-upload"
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(event) => setFile(event.target.files[0])}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    required
                                />
                                <div className="flex flex-col items-center gap-2 pointer-events-none">
                                    <CloudArrowUp
                                        size={48}
                                        className="text-mono-400 group-hover:text-black dark:group-hover:text-white transition-colors"
                                    />
                                    {file ? (
                                        <div className="text-black dark:text-white font-medium truncate max-w-full px-4">
                                            {file.name}
                                        </div>
                                    ) : (
                                        <div className="text-mono-500 text-sm">
                                            Clique ou arraste o PDF do romaneio aqui
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <Button type="submit" className="w-full" size="lg" loading={loading} disabled={!file}>
                            Analisar carga
                        </Button>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default OutboundCreatePage;
